import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';

interface OfferItem {
  id: string;
  title?: string;
  reason?: string;
  image?: string | null;
  url?: string;
}

interface OffersWidgetProps {
  id?: number;
  heading?: string;
  count?: number;
  // Supplied by the GetOffersWidget handler (server-side). Optional so the
  // component still works with sensible fallbacks if wired without a handler.
  apiBase?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const w = (typeof window !== 'undefined' ? window : {}) as any;

// Same consent signals inchcape-cdp.js reads (explicit hook, then Ketch "analytics").
function hasConsent(): boolean {
  try {
    const fn = w.CdpRecsConsent || w.SubaruRecsConsent; // brand-neutral, legacy fallback
    if (typeof fn === 'function') return fn() === true;
  } catch {
    /* ignore */
  }
  try {
    if (w.ketchConsent && typeof w.ketchConsent.analytics !== 'undefined')
      return w.ketchConsent.analytics === true;
  } catch {
    /* ignore */
  }
  return false;
}

// The visitor fingerprint the CDP exposes (== cdp_events.user_id). Brand-neutral name
// (window.CdpUserId) with a legacy Subaru* fallback during the transition.
function cdpUserId(): string | null {
  return w.CdpUserId || w.SubaruCdpUserId || null;
}

function pushCDP(payload: unknown): void {
  try {
    if (typeof w.pushDigitalData === 'function') w.pushDigitalData(payload);
  } catch {
    /* analytics must never break the widget */
  }
}

export const OffersWidget = (props: OffersWidgetProps): React.ReactElement => {
  const heading = props.heading || 'Offers picked for you';
  const count = props.count || 4;
  const apiBase = (
    props.apiBase ||
    w.RecommendationsApiBase ||
    'https://ai-api-staging.inchcape-web.au'
  ).replace(/\/$/, '');

  const [items, setItems] = useState<OfferItem[]>([]);
  const [ready, setReady] = useState(false);

  // Offers are personalization-only: without consent AND the fingerprint there is
  // no fallback (unlike model recs), so the widget simply renders nothing. Resolve
  // both signals event-driven — same approach as the recommendations widget, since
  // Ketch's consent event fires seconds after mount on cold loads — then fetch once.
  useEffect(() => {
    let cancelled = false;
    let fetched = false;

    function fetchOffers(uid: string) {
      if (fetched || cancelled) return;
      fetched = true;
      fetch(
        `${apiBase}/recommendations/offers-for-user/${encodeURIComponent(
          uid
        )}?num=${encodeURIComponent(String(count))}&consent=true`,
        { credentials: 'omit', headers: { Accept: 'application/json' } }
      )
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((data) => {
          if (cancelled) return;
          setItems((data && data.items) || []);
          setReady(true);
        })
        .catch(() => {
          if (!cancelled) setReady(true);
        });
    }

    // Ketch replays the current consent to new subscribers, so read analytics from
    // the event itself (authoritative) rather than racing a timer against globals.
    let ketchAnalytics: boolean | null = null; // null = not yet resolved
    function consentGranted(): boolean {
      if (ketchAnalytics !== null) return ketchAnalytics;
      return hasConsent();
    }

    // Fetch as soon as BOTH consent (analytics) and the fingerprint are known.
    function tryFetch(): void {
      if (fetched || cancelled) return;
      if (!consentGranted()) return;
      const uid = cdpUserId();
      if (uid) fetchOffers(uid);
    }

    const onConsent = (c: { purposes?: Record<string, boolean> }) => {
      ketchAnalytics = c?.purposes?.analytics === true;
      tryFetch();
    };
    const onFingerprint = () => tryFetch();
    try {
      if (typeof w.ketch === 'function') w.ketch('on', 'consent', onConsent);
    } catch {
      /* ignore */
    }
    window.addEventListener('CdpUserIdReady', onFingerprint);
    window.addEventListener('SubaruCdpUserIdReady', onFingerprint); // legacy alias

    // Both signals may already be resolved.
    tryFetch();

    return () => {
      cancelled = true;
      window.removeEventListener('CdpUserIdReady', onFingerprint);
      window.removeEventListener('SubaruCdpUserIdReady', onFingerprint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Emit one CDP impression event once the cards are shown.
  useEffect(() => {
    if (ready && items.length) {
      pushCDP({
        event: '_offerImpression',
        recommendation: {
          source: 'personalize',
          type: 'offer',
          items: items.map((it) => it.id),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, items]);

  // Stable root for hydration; content appears only once loaded with items.
  return (
    <div
      className={styles.offers}
      id={props.id ? `cid-${props.id}` : undefined}
    >
      {ready && items.length > 0 && (
        <div className={styles.container}>
          <h2 className={styles.heading}>{heading}</h2>
          <div className={styles.grid}>
            {items.map((it, i) => (
              <a
                key={it.id || i}
                className={styles.card}
                href={it.url || '#'}
                data-offer-id={it.id || ''}
                onClick={() =>
                  pushCDP({
                    event: '_offerClick',
                    recommendation: {
                      id: it.id,
                      position: i + 1,
                      source: 'personalize',
                      type: 'offer',
                    },
                  })
                }
              >
                {it.image ? (
                  <img
                    className={styles.img}
                    src={it.image}
                    alt={it.title || ''}
                    loading="lazy"
                  />
                ) : null}
                <div className={styles.body}>
                  <span className={styles.tag}>Offer</span>
                  <p className={styles.title}>{it.title || it.id}</p>
                  {it.reason ? (
                    <p className={styles.reason}>{it.reason}</p>
                  ) : null}
                  <span className={styles.cta}>View offer →</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
